// ---------------------------------------------------------------------------
// GraderEvaluateTab — in-place evaluation runner for a single grader.
//
// Mirrors the Eval Template page (query/data sets, graders, variables,
// concurrency + a Jobs rail), but the *subject under test* is pinned to the
// grader this tab belongs to: it's adapted into the agent slot via the
// grader-as-agent pattern (GraderAsAgentSpec). Only the evaluating grader(s)
// and inputs are chosen here.
//
// There's no grader/agent-first run API: a run always resolves a template, and
// a template task references its subject by *agent name*. So "Run evaluation"
// upserts two backing resources, then queues a run against the template:
//   1. a GraderAsAgent agent that adapts this grader (PUT — create-or-replace)
//   2. a template whose single task pins that agent and whose graders[] holds
//      *both* graders: the grader under test (so the adapter can resolve it)
//      and the chosen evaluating grader(s).
// Both are named deterministically per grader. The Jobs rail lists that
// template's runs (filtered by the run displayName, which we set to the
// backing-resource name).
//
// This file owns orchestration only; the two body halves live in
// GraderEvaluateFormColumn and the reused EvaluateJobsRail.
// ---------------------------------------------------------------------------

import { Spinner } from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useHttpClient } from '../../../../api/HttpClientContext.js';
import { useEvaluationRunHistory } from '../../../../hooks/useEvaluationRunHistory/useEvaluationRunHistory.js';
import { AgentsService } from '../../../../services/agentsService.js';
import { EvaluationRunsService } from '../../../../services/evaluationRunsService.js';
import { EvaluationTemplateNotFoundError } from '../../../../services/evaluationTemplatesService.interface.js';
import { EvaluationTemplatesService } from '../../../../services/evaluationTemplatesService.js';
import {
  DEFAULT_GRADER_MODEL,
  type GraderType,
} from '../../../../services/grader.interface.js';
import { WorkflowApiService } from '../../../../services/workflowApi.js';
import {
  isValidAgentName,
  type UpsertAgentBody,
} from '../../../../types/agent.js';
import { isValidEvaluationTemplateName } from '../../../../types/evaluationTemplate.js';
import { EvaluateJobsRail } from '../../../AgentsPage/AgentDetailView/AgentEvaluateTab/EvaluateJobsRail.js';
import { JobResultDialog } from '../../../EvaluationTemplatesPage/EvaluationTemplateDetailView/JobResultDialog.js';
import {
  addBindingFlowError,
  analyzeBindingFlow,
  collectBindingVariables,
  variableHasDefault,
} from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/bindingFlow.utils.js';
import type { BindingFlowResource } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/bindingFlow.utils.js';
import { defaultBindingFor } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/bindings.utils.js';
import type { GraderSummary } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.types.js';
import type {
  EvaluationTemplateWithConfig,
  SeededFormState,
  TaskDraft,
} from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.types.js';
import {
  buildTemplateBody,
  makeTask,
  parseInlineItems,
} from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.utils.js';
import { getFormValidity } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.validation.js';
import { useBindingRows } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useBindingRows.js';
import { useEvaluationTemplateCatalogs } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useEvaluationTemplateCatalogs.js';
import { useFileSource } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useFileSource.js';
import { useFormFields } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useFormFields.js';
import { useTaskDrafts } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useTaskDrafts.js';

import { GraderEvaluateFormColumn } from './GraderEvaluateFormColumn.js';
import { useStyles } from './GraderEvaluateTab.style.js';
import {
  adaptGraderSubjectForAgent,
  assembleTemplateBody,
  backingResourceName,
  graderDefaultBindingFor,
  seedFromBackingTemplate,
} from './GraderEvaluateTab.utils.js';

/**
 * Run controls the tab hands up to its parent so the primary action can live
 * in the grader's title bar (matching the Eval Template page, where the run
 * action sits in the title bar rather than floating above the body).
 */
export interface GraderEvaluateControls {
  canRun: boolean;
  running: boolean;
  run: () => void;
}

export interface GraderEvaluateTabProps {
  workspaceName: string;
  graderName: string;
  /** Reports the run button's state up to the title bar; null when unmounting. */
  onControlsChange?: (controls: GraderEvaluateControls | null) => void;
}

function isNumericComparisonGrader(grader: GraderSummary): boolean {
  const spec = grader.spec;
  return (
    typeof spec === 'object' &&
    spec !== null &&
    'graderType' in spec &&
    spec.graderType === ('NumericComparisonGraderSpec' satisfies GraderType)
  );
}

// Outer wrapper: resolve this grader's durable backing template *before*
// mounting the form, so the form's useState seeds populate from the saved
// config on the first render. A missing template (first-time) seeds blank.
export function GraderEvaluateTab(props: GraderEvaluateTabProps) {
  const { workspaceName, graderName } = props;
  const styles = useStyles();
  const httpClient = useHttpClient();
  const templatesService = useMemo(
    () => new EvaluationTemplatesService(httpClient),
    [httpClient],
  );
  const resourceName = useMemo(
    () => backingResourceName(graderName),
    [graderName],
  );

  const [seedState, setSeedState] = useState<{
    loading: boolean;
    seeded: SeededFormState | null;
    graderModel: string;
    resourceName: string;
  }>({
    loading: true,
    seeded: null,
    graderModel: DEFAULT_GRADER_MODEL,
    resourceName,
  });

  useEffect(() => {
    let cancelled = false;
    setSeedState({
      loading: true,
      seeded: null,
      graderModel: DEFAULT_GRADER_MODEL,
      resourceName,
    });
    templatesService
      .get(workspaceName, resourceName)
      .then((template) => {
        if (cancelled) return;
        const { seed, graderModel } = seedFromBackingTemplate(
          template as EvaluationTemplateWithConfig,
          graderName,
          resourceName,
        );
        setSeedState({
          loading: false,
          seeded: seed,
          graderModel,
          resourceName,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A 404 is the expected first-time case: start with a blank form. Other
        // failures degrade to the same blank form (the user can reconfigure and
        // re-run), matching the prior always-blank behavior.
        if (!(err instanceof EvaluationTemplateNotFoundError)) {
          console.error('Failed to load backing evaluation template', err);
        }
        setSeedState({
          loading: false,
          seeded: null,
          graderModel: DEFAULT_GRADER_MODEL,
          resourceName,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [templatesService, workspaceName, resourceName, graderName]);

  // Guard the stale window: when graderName changes, seedState still holds the
  // previous grader's seed until the effect above re-runs. Rendering the body
  // (keyed by the new resourceName) with that stale seed would mount it with
  // the wrong backing config for one commit, so wait until the loaded seed
  // matches the current resourceName.
  if (seedState.loading || seedState.resourceName !== resourceName) {
    return (
      <div className={styles.root}>
        <Spinner label="Loading evaluation…" />
      </div>
    );
  }

  // Key by resourceName so switching graders remounts the body with fresh seeds.
  return (
    <GraderEvaluateTabBody
      key={resourceName}
      {...props}
      seeded={seedState.seeded}
      initialGraderModel={seedState.graderModel}
    />
  );
}

interface GraderEvaluateTabBodyProps extends GraderEvaluateTabProps {
  seeded: SeededFormState | null;
  initialGraderModel: string;
}

function GraderEvaluateTabBody({
  workspaceName,
  graderName,
  onControlsChange,
  seeded,
  initialGraderModel,
}: GraderEvaluateTabBodyProps) {
  const styles = useStyles();
  const httpClient = useHttpClient();

  const agentsService = useMemo(
    () => new AgentsService(httpClient),
    [httpClient],
  );
  const templatesService = useMemo(
    () => new EvaluationTemplatesService(httpClient),
    [httpClient],
  );
  const runsService = useMemo(
    () => new EvaluationRunsService(httpClient),
    [httpClient],
  );
  const workflowService = useMemo(
    () => new WorkflowApiService(httpClient),
    [httpClient],
  );

  const resourceName = useMemo(
    () => backingResourceName(graderName),
    [graderName],
  );

  // ── Form fields (query/data set, concurrency, variables) ──────────────
  const fields = useFormFields(seeded);

  // Single task with the subject pinned to this page's grader-as-agent adapter.
  const initialDrafts = useMemo(() => {
    const task = makeTask('default');
    return [{ ...task, agent: { ...task.agent, name: resourceName } }];
  }, [resourceName]);

  // Always re-pin the primary task to this page's grader-as-agent adapter. The
  // seeded backing template is a first-class eval template that can be edited
  // elsewhere (e.g. the Eval Templates page), so its persisted task agent may
  // differ from this tab's resourceName; the Evaluate tab is contractually
  // pinned to this grader's adapter.
  const draftsForHook = useMemo<TaskDraft[]>(() => {
    const drafts = seeded?.taskDrafts ?? initialDrafts;
    return drafts.map((draft, index) =>
      index === 0
        ? { ...draft, agent: { ...draft.agent, name: resourceName } }
        : draft,
    );
  }, [resourceName, initialDrafts, seeded?.taskDrafts]);

  const { taskDrafts, addGraderRow, removeGraderRow } = useTaskDrafts({
    initialDrafts: draftsForHook,
  });
  const primaryTask = taskDrafts[0]!;
  const selectedGraderNames = useMemo(
    () => [
      graderName,
      ...primaryTask.graderRows
        .map((grader) => grader.name)
        .filter((name) => name.length > 0),
    ],
    [graderName, primaryTask.graderRows],
  );

  // ── Catalogs (subject + evaluating graders) ───────────────────────────
  const { uniqueGraders, gradersLoading, gradersError } =
    useEvaluationTemplateCatalogs({
      viewOnly: false,
      activeWorkspaceName: workspaceName,
      selectedGraderNames,
    });

  // A grader evaluation compares the grader-under-test's score with a golden
  // expected score, so only NumericComparisonGraderSpec can be selected as the
  // evaluator. The subject grader remains in the full catalog for resolution.
  const evaluatorGraders = useMemo(
    () => uniqueGraders.filter(isNumericComparisonGrader),
    [uniqueGraders],
  );
  const subjectGrader = uniqueGraders.find(
    (candidate) => candidate.name === graderName,
  );
  const adaptedSubject = useMemo(
    () =>
      subjectGrader === undefined
        ? null
        : adaptGraderSubjectForAgent(
            subjectGrader.spec,
            subjectGrader.variables,
          ),
    [subjectGrader],
  );
  const resolvedTaskDrafts = useMemo(
    () =>
      taskDrafts.map((task) => ({
        ...task,
        graderRows: task.graderRows.map((graderRow) => {
          const grader = evaluatorGraders.find(
            (candidate) => candidate.name === graderRow.name,
          );
          return {
            ...graderRow,
            version: grader?.version ?? graderRow.version,
          };
        }),
      })),
    [evaluatorGraders, taskDrafts],
  );

  // ── File-mode pickers (query + data set) ──────────────────────────────
  const queryFile = useFileSource();
  const dataFile = useFileSource();

  const querySetItems = useMemo<string[] | null>(() => {
    if (fields.querySetMode === 'inline') {
      return parseInlineItems(fields.queryInlineText);
    }
    if (fields.querySetMode === 'file') return queryFile.items;
    return null;
  }, [fields.querySetMode, fields.queryInlineText, queryFile.items]);

  const dataSetItems = useMemo<string[] | null>(() => {
    if (!fields.dataSetEnabled) return null;
    if (fields.dataSetMode === 'inline') {
      return parseInlineItems(fields.dataInlineText);
    }
    if (fields.dataSetMode === 'file') return dataFile.items;
    return null;
  }, [
    fields.dataSetEnabled,
    fields.dataSetMode,
    fields.dataInlineText,
    dataFile.items,
  ]);

  const bindingResources = useMemo<BindingFlowResource[]>(() => {
    const resources: BindingFlowResource[] = [];
    if (subjectGrader !== undefined) {
      resources.push({
        kind: 'Grader as Agent',
        name: subjectGrader.name,
        version: subjectGrader.version,
        spec: adaptedSubject?.spec ?? subjectGrader.spec,
        variables: adaptedSubject?.variables ?? subjectGrader.variables,
      });
    }
    for (const graderRow of primaryTask.graderRows) {
      const evaluator = evaluatorGraders.find(
        (candidate) => candidate.name === graderRow.name,
      );
      if (evaluator !== undefined) {
        resources.push({
          kind: 'Grader',
          name: evaluator.name,
          version: evaluator.version,
          spec: evaluator.spec,
          variables: evaluator.variables,
        });
      }
    }
    return resources;
  }, [adaptedSubject, evaluatorGraders, primaryTask.graderRows, subjectGrader]);

  const bindingVariables = useMemo(
    () => collectBindingVariables(fields.variables, bindingResources),
    [bindingResources, fields.variables],
  );
  const getDefaultBinding = useCallback(
    (name: string) => {
      if (variableHasDefault(name, fields.variables, bindingResources)) {
        return '';
      }
      // The wrapped grader runs during the Agent phase, before $.turn.output
      // exists. Its own inputs must therefore come from QuerySet/DataSet data.
      if (adaptedSubject?.variables?.[name] !== undefined) {
        return defaultBindingFor(name);
      }
      return graderDefaultBindingFor(name);
    },
    [adaptedSubject, bindingResources, fields.variables],
  );

  // ── Run + jobs state ──────────────────────────────────────────────────
  const [running, setRunning] = useState(false);
  const [graderModel, setGraderModel] = useState(initialGraderModel);
  const [runError, setRunError] = useState<string | null>(null);
  const [resultRunId, setResultRunId] = useState<string | null>(null);

  // Per-variable runtime bindings, binding flow, and the Jobs rail.
  const { bindingRows, setBinding } = useBindingRows(bindingVariables, {
    getDefault: getDefaultBinding,
    initialOverrides: seeded?.bindings,
  });
  const bindingFlow = useMemo(() => {
    let analysis = analyzeBindingFlow({
      resources: bindingResources,
      templateVariables: fields.variables,
      bindings: bindingRows,
      queryItems: querySetItems,
      dataItems: dataSetItems,
      dataSetEnabled: fields.dataSetEnabled,
      dataSetInspectable: fields.dataSetMode !== 'existing',
    });
    if (gradersError !== null && gradersError !== undefined) {
      analysis = addBindingFlowError(
        analysis,
        'catalog-resolution',
        `Unable to resolve selected component snapshots: ${gradersError}`,
      );
    }
    if (
      !gradersLoading &&
      (gradersError === null || gradersError === undefined)
    ) {
      if (subjectGrader === undefined) {
        analysis = addBindingFlowError(
          analysis,
          'missing-subject-grader',
          `Grader ${graderName} could not be resolved in this workspace.`,
        );
      }
      for (const graderRow of primaryTask.graderRows) {
        if (graderRow.name.length === 0) continue;
        const selected = uniqueGraders.find(
          (grader) => grader.name === graderRow.name,
        );
        if (selected !== undefined && !isNumericComparisonGrader(selected)) {
          analysis = addBindingFlowError(
            analysis,
            `non-numeric-grader-${graderRow.name}`,
            `Evaluating grader ${graderRow.name} must use NumericComparisonGraderSpec.`,
          );
        } else if (selected === undefined) {
          analysis = addBindingFlowError(
            analysis,
            `missing-grader-${graderRow.name}`,
            `Grader ${graderRow.name} could not be resolved in this workspace.`,
          );
        }
      }
    }
    return analysis;
  }, [
    bindingResources,
    bindingRows,
    dataSetItems,
    fields.dataSetEnabled,
    fields.dataSetMode,
    fields.variables,
    querySetItems,
    gradersError,
    graderName,
    gradersLoading,
    primaryTask.graderRows,
    subjectGrader,
    uniqueGraders,
  ]);
  const runs = useEvaluationRunHistory({
    workspaceName,
    resourceName,
    runsService,
  });

  const { canSubmit } = getFormValidity({
    jobName: resourceName,
    taskDrafts: resolvedTaskDrafts,
    variables: fields.variables,
    querySetMode: fields.querySetMode,
    querySetItems,
    dataSetEnabled: fields.dataSetEnabled,
    dataSetMode: fields.dataSetMode,
    dataSetExistingId: fields.dataSetExistingId,
    dataSetFileSelector: fields.dataSetFileSelector,
    dataSetItems,
    bindingFlowHasErrors: bindingFlow.hasErrors || gradersLoading,
    submitting: running,
    workspaceLoading: false,
    activeWorkspaceName: workspaceName,
  });

  const selectedGraderCount = primaryTask.graderRows.filter(
    (r) => r.name.length > 0,
  ).length;
  // The whole point of this tab is "evaluate this grader with a grader", so —
  // unlike the general eval-template form — at least one evaluating grader is
  // required, and the backing agent + template names must be valid.
  const canRun =
    canSubmit &&
    selectedGraderCount > 0 &&
    isValidAgentName(resourceName) &&
    isValidEvaluationTemplateName(resourceName);

  const handleRun = useCallback(async () => {
    if (!canRun) return;
    setRunError(null);
    setRunning(true);
    try {
      let graderUnderTestName = graderName;
      if (adaptedSubject?.requiresClone === true) {
        const clone = await workflowService.upsertGrader(
          workspaceName,
          resourceName,
          {
            displayName: `Eval adapter for ${graderName}`,
            description: `Evaluation-only copy of ${graderName} with Agent-phase input bindings.`,
            spec: adaptedSubject.spec,
            variables: adaptedSubject.variables,
          },
        );
        if (clone.isDraft === true) {
          await workflowService.snapshotGrader(workspaceName, resourceName);
        }
        graderUnderTestName = resourceName;
      }

      // 1. PUT (create-or-replace) the grader-as-agent adapter that promotes
      //    this grader into the subject-under-test slot.
      const agentBody: UpsertAgentBody = {
        displayName: `Grader under test: ${graderName}`,
        description: `Adapts grader '${graderName}' as the subject under test (grader-as-agent).`,
        spec: {
          agentType: 'GraderAsAgentSpec',
          init: { graderName: graderUnderTestName },
          invoke: {},
        },
      };
      const adapterAgent = await agentsService.replace(
        workspaceName,
        resourceName,
        agentBody,
      );
      // Hosted runs resolve the adapter agent's latest *snapshot*, not the
      // working copy, so snapshot a draft before it is referenced by the run.
      const adapterVersion = adapterAgent.isDraft
        ? (await agentsService.snapshot(workspaceName, resourceName)).version
        : Math.max(
            ...(
              await agentsService.listVersions(workspaceName, resourceName)
            ).map((version) => version.version),
          );
      if (!Number.isFinite(adapterVersion)) {
        throw new Error(
          `Adapter Agent ${resourceName} has no snapshot available.`,
        );
      }

      // 2. PUT (create-or-replace) the backing template whose task pins the
      //    adapter as its agent and applies the graders. The grader under test
      //    is added to the task's graders alongside the chosen evaluating
      //    grader(s): the run config's graders[] must contain both — the grader
      //    being graded (so the grader-as-agent adapter resolves it) and the
      //    grader doing the evaluation.
      const taskDraftsForRun = resolvedTaskDrafts.map((t, i) =>
        i === 0
          ? {
              ...t,
              agent: { ...t.agent, version: adapterVersion },
              graderRows: [
                ...t.graderRows,
                {
                  id: `subject-${t.id}`,
                  name: graderUnderTestName,
                  version: adaptedSubject?.requiresClone
                    ? undefined
                    : subjectGrader?.version,
                },
              ],
            }
          : t,
      );
      const parts = buildTemplateBody({
        taskDrafts: taskDraftsForRun,
        variables: fields.variables,
        querySetItems,
        dataSetEnabled: fields.dataSetEnabled,
        dataSetMode: fields.dataSetMode,
        dataSetItems,
        dataSetExistingId: fields.dataSetExistingId,
        dataSetExistingVersion: fields.dataSetExistingVersion,
        dataSetFileSelector: fields.dataSetFileSelector,
        workersText: fields.workersText,
        parallelism: fields.parallelism,
      });
      // Declare the GRADER_MODEL run input and emit the with: bindings the base
      // builder omits (see GraderEvaluateTab.utils).
      const body = assembleTemplateBody({
        graderName,
        parts,
        graderModel,
        bindingRows,
      });
      const template = await templatesService.replace(
        workspaceName,
        resourceName,
        body,
      );
      if (template.isDraft) {
        await templatesService.snapshot(workspaceName, resourceName);
      }

      // 3. Queue a run. The run's displayName is the resource name so the Jobs
      //    rail can filter this grader's runs out of the workspace-wide list.
      const run = await runsService.queue(workspaceName, {
        runOrigin: 'hosted',
        templateName: resourceName,
        displayName: resourceName,
      });
      runs.prependRun(run);
      runs.refresh();
    } catch (err) {
      setRunError(
        err instanceof Error ? err.message : 'Failed to start evaluation.',
      );
    } finally {
      setRunning(false);
    }
  }, [
    canRun,
    graderName,
    graderModel,
    bindingRows,
    workspaceName,
    resourceName,
    agentsService,
    templatesService,
    runsService,
    runs,
    workflowService,
    adaptedSubject,
    resolvedTaskDrafts,
    subjectGrader?.version,
    querySetItems,
    dataSetItems,
    // Depend on the individual field values handleRun reads, not the whole
    // `fields` object — useFormFields returns a fresh object each render, so
    // depending on it would recreate handleRun every render and (via the
    // controls effect) drive an unbounded parent re-render loop.
    fields.variables,
    fields.dataSetEnabled,
    fields.dataSetMode,
    fields.dataSetExistingId,
    fields.dataSetExistingVersion,
    fields.dataSetFileSelector,
    fields.workersText,
    fields.parallelism,
  ]);

  // Surface the run button's state to the title bar. Re-reports whenever the
  // gate or handler changes; clears on unmount (tab switch) so the title bar
  // drops the button.
  useEffect(() => {
    onControlsChange?.({ canRun, running, run: () => void handleRun() });
  }, [onControlsChange, canRun, running, handleRun]);
  useEffect(() => () => onControlsChange?.(null), [onControlsChange]);

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <GraderEvaluateFormColumn
          graderName={graderName}
          disabled={running}
          graderModel={graderModel}
          onGraderModelChange={setGraderModel}
          bindings={bindingRows}
          bindingFlow={bindingFlow}
          onBindingChange={setBinding}
          fields={fields}
          queryFile={queryFile}
          dataFile={dataFile}
          task={primaryTask}
          uniqueGraders={evaluatorGraders}
          gradersLoading={gradersLoading}
          onAddGraderRow={(name) => addGraderRow(primaryTask.id, name)}
          onRemoveGraderRow={(rowId) => removeGraderRow(primaryTask.id, rowId)}
          runError={runError}
        />

        <EvaluateJobsRail
          runHistory={runs.runHistory}
          loading={runs.loading}
          error={runs.error}
          cancellingId={runs.cancellingId}
          onCancelRun={(runId) => runs.cancelRun(runId)}
          onOpenResult={setResultRunId}
        />
      </div>

      <JobResultDialog
        workspaceName={workspaceName}
        runId={resultRunId}
        runsService={runsService}
        onClose={() => setResultRunId(null)}
      />
    </div>
  );
}
